import { chipClasses } from "@mui/material/Chip";
import { dividerClasses } from "@mui/material/Divider";
import { iconButtonClasses } from "@mui/material/IconButton";
import { inputBaseClasses } from "@mui/material/InputBase";
import { menuItemClasses } from "@mui/material/MenuItem";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import { selectClasses } from "@mui/material/Select";
import { svgIconClasses } from "@mui/material/SvgIcon";
import { tabClasses } from "@mui/material/Tab";
import { toggleButtonClasses } from "@mui/material/ToggleButton";
import { Components, Theme, alpha } from "@mui/material/styles";

import AppIcon from "@/components/Core/AppIcon";
import { hexToRgba } from "@/utils";

import { brand, colors, gray, red } from "../themePrimitives";

type ColorScale = typeof gray;

/**
 * An outlined button: a line and its ink, on nothing.
 *
 * It used to be filled as well - a pale wash in the light scheme, a murky dark
 * block of the scale in the dark one - which made "outlined" a second, muddier
 * kind of filled button and left it heavier than the contained button beside
 * it on a dark page. Now the fill only comes with the pointer.
 */
const outlinedButtonVariant = (theme: Theme, scale: ColorScale) => ({
  color: scale[700],
  border: "1px solid",
  borderColor: scale[300],
  backgroundColor: "transparent",
  "&:hover": {
    backgroundColor: alpha(scale[400], 0.1),
    borderColor: scale[400],
  },
  "&:active": {
    backgroundColor: alpha(scale[400], 0.18),
  },
  ...theme.applyStyles("dark", {
    color: scale[200],
    border: "1px solid",
    borderColor: alpha(scale[400], 0.55),
    backgroundColor: "transparent",
    "&:hover": {
      borderColor: scale[400],
      backgroundColor: alpha(scale[400], 0.16),
    },
    "&:active": {
      backgroundColor: alpha(scale[400], 0.24),
    },
  }),
});

const CHIP_SOLID_COLORS = ["success", "warning", "info", "error"] as const;

const chipSolidVariant = (theme: Theme, color: (typeof CHIP_SOLID_COLORS)[number]) => ({
  border: "none",
  backgroundColor: theme.palette[color].main,
  [`& .${chipClasses.label}`]: {
    color: theme.palette[color].contrastText,
  },
  [`& .${chipClasses.icon}`]: {
    color: theme.palette[color].contrastText,
  },
});

const alertSeverityVariant = (severity: "success" | "warning", color: string) => ({
  props: {
    severity,
  },
  style: {
    backgroundColor: hexToRgba(color, 8),
    color,
    border: `1px solid ${color}`,
    "& .MuiAlert-icon": {
      color,
    },
  },
});

/** The outline the mascot's own cloud is drawn from, as one closed path. */
const CLOUD_PATH = "M8 23a8 8 0 0 1 .9-15.8 11 11 0 0 1 20.7-2A8 8 0 0 1 38 23Z";

/**
 * The texture every page sits on: a drifting sky, at the weight of a watermark.
 *
 * Two clouds per tile at different scales, offset from each other, so the
 * repeat reads as weather rather than as a grid. At 9% opacity it is below the
 * threshold of a pattern you notice and above the one where the background is
 * flat - which is the whole job.
 */
export const cloudPatternUrl = (color: string) => {
  const svg =
    `<svg width='112' height='76' xmlns='http://www.w3.org/2000/svg'>` +
    `<g fill='none' stroke='${color}' stroke-opacity='0.09' stroke-width='1.5' stroke-linejoin='round'>` +
    `<path d='${CLOUD_PATH}' transform='translate(4 4)'/>` +
    `<path d='${CLOUD_PATH}' transform='translate(62 40) scale(.8)'/>` +
    `</g></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

export const customizations: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundImage: cloudPatternUrl(colors.prime),
      },
    },
  },
  MuiContainer: {
    styleOverrides: {
      root: ({ theme }) => ({
        // MUI's default 24px gutter from sm up wastes width on tablets and desktop.
        [theme.breakpoints.up("sm")]: {
          padding: 4,
        },
      }),
    },
  },
  MuiButtonBase: {
    styleOverrides: {
      root: {
        boxSizing: "border-box",
        transition: "all 100ms ease-in",
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: "none",
        borderRadius: theme.shape.borderRadius,
        textTransform: "none",
        variants: [
          {
            props: {
              size: "small",
            },
            style: {
              height: "2.25rem",
              padding: "8px 12px",
            },
          },
          {
            props: {
              size: "medium",
            },
            style: {
              height: "2.5rem",
            },
          },
          {
            props: {
              color: "primary",
              variant: "contained",
            },
            style: {
              color: colors.white,
              backgroundImage: colors.gradient,
              "&.Mui-disabled": {
                color: gray[500],
                ...theme.applyStyles("dark", {
                  color: gray[600],
                }),
              },
            },
          },
          {
            props: {
              color: "success",
              variant: "contained",
            },
            style: {
              color: colors.white,
              backgroundImage: `linear-gradient(180deg, #34A853 0%, #067524 100%)`,
              "&.Mui-disabled": {
                color: gray[500],
                ...theme.applyStyles("dark", {
                  color: gray[600],
                }),
              },
            },
          },
          {
            props: {
              color: "warning",
              variant: "contained",
            },
            style: {
              // Amber is the warning colour now rather than a second brand
              // tone, so it stays where it was while `primary` moved to blue.
              color: colors.white,
              backgroundImage: `linear-gradient(180deg, #FFB52E 0%, #F59E0B 100%)`,
              ...theme.applyStyles("dark", {
                color: "black",
              }),
              "&.Mui-disabled": {
                color: gray[500],
                ...theme.applyStyles("dark", {
                  color: gray[600],
                }),
              },
            },
          },
          {
            props: {
              color: "inherit",
              variant: "contained",
            },
            style: {
              color: colors.dark,
              background: colors.white,
              boxShadow: "0px 1px 4px rgba(12, 12, 13, 0.05)",
              border: `1px solid ${colors.border}`,
              ...theme.applyStyles("dark", {
                border: `1px solid ${colors.borderDark}`,
                background: colors.layer2Dark,
                color: colors.white,
              }),
            },
          },
          {
            props: {
              color: "secondary",
              variant: "outlined",
            },
            style: outlinedButtonVariant(theme, gray),
          },
          {
            props: {
              color: "error",
              variant: "outlined",
            },
            style: outlinedButtonVariant(theme, red),
          },
          {
            props: {
              color: "primary",
              variant: "outlined",
            },
            style: outlinedButtonVariant(theme, brand),
          },
          {
            props: {
              color: "inherit",
              variant: "outlined",
            },
            style: {
              borderColor: colors.border,
              ...theme.applyStyles("dark", {
                borderColor: colors.borderDark,
              }),
            },
          },
          {
            props: {
              color: "primary",
              variant: "text",
            },
            style: {
              color: colors.prime,
            },
          },
          {
            props: {
              color: "secondary",
              variant: "text",
            },
            style: {
              color: brand[700],
              "&:hover": {
                backgroundColor: alpha(brand[100], 0.5),
              },
              "&:active": {
                backgroundColor: alpha(brand[200], 0.7),
              },
              ...theme.applyStyles("dark", {
                color: brand[100],
                "&:hover": {
                  backgroundColor: alpha(brand[900], 0.5),
                },
                "&:active": {
                  backgroundColor: alpha(brand[900], 0.3),
                },
              }),
            },
          },
        ],
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        boxShadow: "none",
        borderRadius: theme.shape.borderRadius,
        textTransform: "none",
        fontWeight: theme.typography.fontWeightMedium,
        letterSpacing: 0,
        color: theme.palette.text.primary,
        border: "1px solid ",
        borderColor: gray[200],
        backgroundColor: alpha(gray[50], 0.3),
        transition:
          "background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease, transform 120ms ease",
        "&:hover": {
          backgroundColor: gray[100],
          borderColor: gray[300],
        },
        // A small give under the finger, so a tap reads as pressed.
        "&:active": {
          backgroundColor: gray[200],
          transform: "scale(0.92)",
        },
        "&:focus-visible": { outline: `2px solid ${colors.prime}`, outlineOffset: 2 },
        [`&.${iconButtonClasses.disabled}`]: {
          opacity: 0.45,
          borderColor: gray[200],
        },
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
          "&:active": { transform: "none" },
        },
        // Inside a field only the icon shows: no box, no fill; the pointer just darkens the ink.
        [`.${inputBaseClasses.root} &`]: {
          border: "none",
          "&, &:hover, &:active": { backgroundColor: "transparent" },
          "&:hover": { color: theme.palette.text.primary },
        },
        ...theme.applyStyles("dark", {
          backgroundColor: colors.layer1Dark,
          borderColor: gray[700],
          "&:hover": {
            backgroundColor: gray[900],
            borderColor: gray[600],
          },
          "&:active": {
            backgroundColor: gray[900],
          },
          [`&.${iconButtonClasses.disabled}`]: { borderColor: gray[700] },
        }),
        variants: [
          {
            props: {
              color: "inherit",
            },
            style: {
              backgroundColor: "transparent !important",
              border: "none",
              borderRadius: "50%",
            },
          },
          {
            props: {
              size: "small",
            },
            style: {
              width: "2.25rem",
              height: "2.25rem",
              padding: "0.25rem",
              [`& .${svgIconClasses.root}`]: { fontSize: "1rem" },
            },
          },
          {
            props: {
              size: "medium",
            },
            style: {
              width: "2.5rem",
              height: "2.5rem",
            },
          },
          {
            props: {
              color: "primary",
            },
            style: {
              borderColor: `${colors.prime} !important`,
              backgroundColor: `${alpha(colors.prime, 0.16)} !important`,
            },
          },
        ],
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: "12px 16px",
        textTransform: "none",
        borderRadius: "10px",
        fontWeight: 500,
        ...theme.applyStyles("dark", {
          color: gray[400],
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.5)",
          [`&.${toggleButtonClasses.selected}`]: {
            color: brand[300],
          },
        }),
      }),
    },
  },
  MuiTypography: {
    // MUI renders subtitles as <h6>; they are styled text, not headings, and skew the page outline for SEO.
    defaultProps: { variantMapping: { subtitle1: "p", subtitle2: "p" } },
    styleOverrides: {
      root: ({ theme }) => ({
        variants: [
          {
            props: { color: "grey" },
            style: {
              color: `${colors.greyDark} !important`,
              ...theme.applyStyles("dark", {
                color: `${colors.greyLight} !important`,
              }),
            },
          },
          {
            props: { color: "grey-dark" },
            style: {
              color: `${colors.greyDark} !important`,
            },
          },
          {
            props: { color: "red" },
            style: {
              color: `${colors.red} !important`,
            },
          },
          {
            props: { color: "default" },
            style: {
              color: `${colors.dark} !important`,
              ...theme.applyStyles("dark", {
                color: `${colors.white} !important`,
              }),
            },
          },
          {
            props: { color: "prime" },
            style: {
              color: `${colors.prime} !important`,
            },
          },
        ],
      }),
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 10,
        height: 16,
        width: 16,
        borderRadius: 5,
        border: "1px solid ",
        borderColor: alpha(gray[300], 0.8),
        boxShadow: "0 0 0 1.5px hsla(210, 0%, 0%, 0.04) inset",
        backgroundColor: alpha(gray[100], 0.4),
        transition: "border-color, background-color, 120ms ease-in",
        "&:hover": {
          borderColor: brand[300],
        },
        "&.Mui-focusVisible": {
          outline: `3px solid ${alpha(brand[500], 0.5)}`,
          outlineOffset: "2px",
          borderColor: brand[400],
        },
        "&.Mui-checked": {
          color: colors.white,
          backgroundColor: brand[500],
          borderColor: brand[500],
          boxShadow: `none`,
          "&:hover": {
            backgroundColor: brand[600],
          },
        },
        ...theme.applyStyles("dark", {
          borderColor: alpha(gray[700], 0.8),
          boxShadow: "0 0 0 1.5px hsl(210, 0%, 0%) inset",
          backgroundColor: alpha(gray[900], 0.8),
          "&:hover": {
            borderColor: brand[300],
          },
          "&.Mui-focusVisible": {
            borderColor: brand[400],
            outline: `3px solid ${alpha(brand[500], 0.5)}`,
            outlineOffset: "2px",
          },
        }),
        variants: [
          {
            props: {
              color: "primary",
            },
            style: {
              "&.Mui-checked": {
                background: colors.gradient,
              },
            },
          },
        ],
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        border: "none",
      },
      input: {
        "&::placeholder": {
          opacity: 0.7,
          color: gray[500],
        },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: ({ theme }) => ({
        padding: 0,
        "&:-webkit-autofill": {
          WebkitBoxShadow: `0 0 0 100px ${theme.palette.mode === "dark" ? colors.layer1Dark : colors.white} inset !important`,
        },
        // A saved login would otherwise show as a lighter patch on the modal.
        ".MuiDialog-paper &:-webkit-autofill": {
          WebkitBoxShadow: `0 0 0 100px ${colors.white} inset !important`,
          ...theme.applyStyles("dark", {
            WebkitBoxShadow: `0 0 0 100px ${colors.dialogDark} inset !important`,
          }),
        },
      }),
      root: ({ theme }) => ({
        padding: "8px 12px",
        color: theme.palette.text.primary,
        borderRadius: theme.shape.borderRadius,
        border: `1px solid`,
        borderColor: brand[400],
        background: "transparent",
        backdropFilter: "blur(1px)",
        transition: "border 120ms ease-in",
        "&:hover": {
          borderColor: gray[400],
        },
        [`&.${outlinedInputClasses.focused}`]: {
          borderColor: brand[400],
        },
        ...theme.applyStyles("dark", {
          background: "transparent",
          "&:hover": {
            borderColor: gray[500],
          },
        }),
        variants: [
          {
            props: {
              size: "small",
            },
            style: {
              height: "2.25rem",
            },
          },
          {
            props: {
              size: "medium",
            },
            style: {
              height: "2.5rem",
            },
          },
        ],
      }),
      notchedOutline: {
        border: "none",
      },
    },
  },
  MuiInputAdornment: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.grey[500],
        ...theme.applyStyles("dark", {
          color: theme.palette.grey[400],
        }),
      }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        typography: theme.typography.caption,
        marginBottom: 0,
        color: colors.grey,
      }),
    },
  },
  MuiList: {
    styleOverrides: {
      root: {
        padding: "8px",
        display: "flex",
        flexDirection: "column",
        gap: 0,
      },
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: ({ theme }) => ({
        fontSize: theme.typography.body2.fontSize,
        fontWeight: 500,
        lineHeight: theme.typography.body2.lineHeight,
      }),
      secondary: ({ theme }) => ({
        fontSize: theme.typography.caption.fontSize,
        lineHeight: theme.typography.caption.lineHeight,
      }),
    },
  },
  MuiListSubheader: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: "transparent",
        padding: "4px 8px",
        fontSize: theme.typography.caption.fontSize,
        fontWeight: 500,
        lineHeight: theme.typography.caption.lineHeight,
      }),
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: 0,
      },
    },
  },
  MuiChip: {
    defaultProps: {
      size: "small",
    },
    styleOverrides: {
      root: ({ theme }) => ({
        border: "1px solid",
        borderRadius: "999px",
        [`& .${chipClasses.label}`]: {
          fontWeight: 600,
        },
        variants: [
          ...CHIP_SOLID_COLORS.map((color) => ({
            props: { color },
            style: chipSolidVariant(theme, color),
          })),
          {
            props: { size: "small" },
            style: {
              maxHeight: 20,
              [`& .${chipClasses.label}`]: {
                fontSize: theme.typography.caption.fontSize,
              },
              [`& .${svgIconClasses.root}`]: {
                fontSize: theme.typography.caption.fontSize,
              },
            },
          },
          {
            props: { size: "medium" },
            style: {
              [`& .${chipClasses.label}`]: {
                fontSize: theme.typography.caption.fontSize,
              },
            },
          },
        ],
      }),
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      actions: {
        display: "flex",
        gap: 8,
        marginRight: 6,
        [`& .${iconButtonClasses.root}`]: {
          minWidth: 0,
          width: 36,
          height: 36,
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.palette.divider,
        background: "transparent",
        backdropFilter: "blur(1px)",
      }),
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        backgroundColor: "transparent",
        backdropFilter: "blur(1px)",
      },
    },
  },
  MuiTableBody: {
    styleOverrides: {
      root: {
        "& .MuiTableRow-root": {
          "&:last-child td, &:last-child th": { border: 0 },
          "&:nth-of-type(odd)": {
            backgroundColor: "transparent",
          },
        },
      },
    },
  },
  MuiTable: {
    styleOverrides: {
      root: ({ theme }) => ({
        minWidth: 650,
        borderColor: colors.borderDark,
        background: "transparent",
        backdropFilter: "blur(1px)",
        boxShadow: "0px 1px 4px rgba(12, 12, 13, 0.05)",
        ...theme.applyStyles("dark", {
          background: "transparent",
        }),
      }),
    },
  },
  MuiIcon: {
    defaultProps: {
      fontSize: "small",
    },
    styleOverrides: {
      root: {
        variants: [
          {
            props: {
              fontSize: "small",
            },
            style: {
              fontSize: "1rem",
            },
          },
        ],
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        padding: "6px 8px",
        [`&.${menuItemClasses.focusVisible}`]: {
          backgroundColor: "transparent",
        },
        [`&.${menuItemClasses.selected}`]: {
          [`&.${menuItemClasses.focusVisible}`]: {
            backgroundColor: alpha(theme.palette.action.selected, 0.3),
          },
        },
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      list: {
        gap: "0px",
        [`&.${dividerClasses.root}`]: {
          margin: "0 -8px",
        },
      },
      paper: ({ theme }) => ({
        marginTop: "4px",
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
        backgroundImage: "none",
        background: colors.white,
        boxShadow:
          "hsla(220, 30%, 5%, 0.07) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.07) 0px 8px 16px -5px",
        ...theme.applyStyles("dark", {
          background: colors.layer1Dark,
          boxShadow:
            "hsla(220, 30%, 5%, 0.7) 0px 4px 16px 0px, hsla(220, 25%, 10%, 0.8) 0px 8px 16px -5px",
        }),
      }),
    },
  },
  MuiSelect: {
    defaultProps: {
      IconComponent: (props) => <AppIcon name="down" {...props} />,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        border: "1px solid",
        borderColor: gray[200],
        background: "transparent",
        backdropFilter: "blur(1px)",
        boxShadow: `inset 0 1px 0 1px hsla(220, 0%, 100%, 0.6), inset 0 -1px 0 1px hsla(220, 35%, 90%, 0.5)`,
        "&:hover": {
          borderColor: gray[300],
          background: "transparent",
          boxShadow: "none",
        },
        [`&.${selectClasses.focused}`]: {
          outlineOffset: 0,
          borderColor: gray[400],
        },
        "&:before, &:after": {
          display: "none",
        },

        ...theme.applyStyles("dark", {
          borderRadius: theme.shape.borderRadius,
          borderColor: gray[700],
          background: "transparent",
          boxShadow: `inset 0 1px 0 1px ${alpha(gray[700], 0.15)}, inset 0 -1px 0 1px hsla(220, 0%, 0%, 0.7)`,
          "&:hover": {
            borderColor: alpha(gray[700], 0.7),
            background: "transparent",
            boxShadow: "none",
          },
          [`&.${selectClasses.focused}`]: {
            outlineOffset: 0,
          },
          "&:before, &:after": {
            display: "none",
          },
        }),
      }),
      select: ({ theme }) => ({
        display: "flex",
        alignItems: "center",
        ...theme.applyStyles("dark", {
          display: "flex",
          alignItems: "center",
        }),
      }),
      icon: {
        top: "50%",
        transform: "translateY(-50%)",
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: "10px",
        minHeight: "fit-content",
        backgroundColor: colors.border,
        ...theme.applyStyles("dark", {
          backgroundColor: colors.bgDark,
        }),
      }),
      indicator: {
        height: "100%",
        borderRadius: "10px",
        background: "transparent",
        border: `1px solid ${colors.prime}`,
      },
      scroller: {
        width: "auto",
      },
      vertical: ({ theme }) => ({
        backgroundColor: "transparent !important",
        "& .MuiTabs-indicator": {
          display: "none !important",
        },
        "& .MuiTab-root:hover": {
          color: colors.dark,
          ...theme.applyStyles("dark", {
            color: colors.white,
          }),
        },
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        margin: 0,
        fontSize: "16px",
        padding: "10px 20px",
        borderRadius: "10px",
        textTransform: "none",
        minWidth: "fit-content",
        minHeight: "fit-content",
        color: theme.palette.text.secondary,
        border: "1px solid",
        borderColor: "transparent",
        ":hover": {
          color: colors.prime,
        },
        [`&.${tabClasses.selected}`]: {
          color: colors.prime,
          backgroundColor: hexToRgba(colors.prime, 8),
          borderColor: colors.prime,
        },
      }),
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 10,
        variants: [
          alertSeverityVariant("success", colors.prime),
          alertSeverityVariant("warning", colors.orange),
        ],
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      root: ({ theme }) => ({
        "& .MuiDialog-paper": {
          width: "unset",
          borderRadius: "10px",
          border: "1px solid",
          borderColor: colors.border,
          // MUI's dark elevation overlay washes the panel to grey.
          backgroundImage: "none",
          ...theme.applyStyles("dark", {
            borderColor: colors.borderDark,
            backgroundColor: colors.dialogDark,
          }),
          [theme.breakpoints.down(426)]: {
            width: "100vw",
            margin: "0 1rem",
            borderRadius: "1rem",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          },
        },
      }),
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: ({ theme }) => ({
        height: 8,
        borderRadius: 8,
        backgroundColor: gray[200],
        ...theme.applyStyles("dark", {
          backgroundColor: gray[700],
        }),
      }),
    },
  },
  /**
   * Loading placeholders, in the brand rather than in MUI's neutral grey.
   *
   * Every list in the app shows these before its data lands, so they are the
   * first thing a visitor sees on most screens - which makes a flat grey the
   * most-seen colour on the site and the wrong one.
   */
  MuiSkeleton: {
    defaultProps: {
      animation: "wave",
    },
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: alpha(colors.prime, 0.07),
        "&::after": {
          background: `linear-gradient(90deg, transparent, ${alpha(colors.prime, 0.12)}, transparent)`,
        },
        ...theme.applyStyles("dark", {
          backgroundColor: alpha(colors.prime, 0.1),
          "&::after": {
            background: `linear-gradient(90deg, transparent, ${alpha(colors.prime, 0.16)}, transparent)`,
          },
        }),
      }),
    },
  },
  MuiAccordion: {
    defaultProps: {
      elevation: 0,
      disableGutters: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 16,
        padding: 4,
        marginTop: 8,
        marginBottom: 8,
        overflow: "clip",
        backgroundColor: theme.palette.background.default,
        border: "1px solid",
        borderColor: theme.palette.divider,
        ":before": {
          backgroundColor: "transparent",
        },
        "&:first-of-type": {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
        },
        "&:last-of-type": {
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
        },
      }),
    },
  },
  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        border: "none",
      },
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: { border: "none" },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => {
        return {
          padding: 16,
          gap: 16,
          transition: "all 100ms ease",
          borderRadius: theme.shape.borderRadius,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "none",
          variants: [
            {
              props: {
                variant: "outlined",
              },
              style: {
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: "none",
              },
            },
          ],
        };
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: 0,
        "&:last-child": { paddingBottom: 0 },
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
};
